import React, { useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Fade,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Category as CategoryIcon,
  Event as EventIcon,
  Palette as PaletteIcon,
  Lock as LockIcon,
  Star as StarIcon,
  AddCircle as AddCircleIcon,
  Tune as TuneIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import AheadIcon from '../components/AheadIcon';
import PricingChart from '../components/PricingChart';

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const howItWorksRef = useRef(null);

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    {
      icon: <CheckCircleIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Focused Task Management',
      description: 'Simple task lists, clear priorities, zero distractions.',
    },
    {
      icon: <CategoryIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Smart Categories & Filters',
      description: 'Organize by life areas, fast search and sorting, instant clarity.',
    },
    {
      icon: <EventIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Recurring Tasks & Due Dates',
      description: 'Never forget important routines, flexible repeat rules.',
    },
    {
      icon: <PaletteIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Minimal, Calm Design',
      description: 'Refined clarity, thoughtful polish, designed to reduce cognitive load.',
    },
    {
      icon: <StarIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Priority & Focus Mode',
      description: 'Set priorities, star important tasks, focus on what matters most.',
    },
    {
      icon: <LockIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Fast & Secure',
      description: 'Modern stack, secure authentication, works everywhere.',
    },
  ];

  const steps = [
    {
      icon: <AddCircleIcon sx={{ fontSize: 56, color: theme.palette.primary.main }} />,
      title: 'Add tasks',
      description: 'Quickly capture what needs to be done.',
    },
    {
      icon: <TuneIcon sx={{ fontSize: 56, color: theme.palette.primary.main }} />,
      title: 'Organize & prioritize',
      description: 'Use categories, filters, and focus mode to stay on track.',
    },
    {
      icon: <CheckCircleIcon sx={{ fontSize: 56, color: theme.palette.primary.main }} />,
      title: 'Complete with confidence',
      description: 'Finish your day knowing what matters most is done.',
    },
  ];

  return (
    <Box sx={{ bgcolor: '#fafbfc', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box
          sx={{
            minHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            py: { xs: 8, md: 12 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Fade in timeout={800}>
            <Box>
              <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                <AheadIcon size={80} />
              </Box>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  mb: 3,
                  color: '#1a1a1a',
                  lineHeight: 1.1,
                }}
              >
                Stay Ahead. Effortlessly.
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                  fontWeight: 400,
                  color: 'rgba(0, 0, 0, 0.65)',
                  mb: 5,
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                A minimalist, powerful task manager designed to help you plan, focus, and finish.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  mb: 4,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={scrollToHowItWorks}
                  endIcon={<ArrowDownwardIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    borderRadius: 2,
                    textTransform: 'none',
                    borderColor: 'rgba(0, 0, 0, 0.2)',
                    color: 'rgba(0, 0, 0, 0.8)',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      bgcolor: 'rgba(25, 118, 210, 0.04)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  See how it works
                </Button>
              </Box>
            </Box>
          </Fade>
        </Box>
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: '#ffffff', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 600,
              textAlign: 'center',
              mb: 6,
              color: '#1a1a1a',
              letterSpacing: '-0.01em',
            }}
          >
            Everything you need to stay organized
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Fade in timeout={600} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                      },
                      transition: 'transform 0.2s ease',
                    }}
                  >
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        color: '#1a1a1a',
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.65)',
                        fontSize: { xs: '0.95rem', md: '1rem' },
                        lineHeight: 1.6,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box
        ref={howItWorksRef}
        sx={{ bgcolor: '#ffffff', py: { xs: 8, md: 12 } }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 600,
              textAlign: 'center',
              mb: 6,
              color: '#1a1a1a',
              letterSpacing: '-0.01em',
            }}
          >
            How it works
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {steps.map((step, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in timeout={600} style={{ transitionDelay: `${index * 150}ms` }}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ mb: 3 }}>{step.icon}</Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: '#1a1a1a',
                        fontSize: { xs: '1.25rem', md: '1.5rem' },
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.65)',
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        lineHeight: 1.6,
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Visual Preview Section */}
      <Box sx={{ bgcolor: '#fafbfc', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      bgcolor: '#ffffff',
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                      position: 'relative',
                      height: { xs: '280px', md: '380px' },
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="/screenshots/task-list.png"
                      alt="Task list interface showing focus mode and task management"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'top center',
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999; background: #f5f5f5; font-size: 16px;">Task List View</div>';
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      bgcolor: '#ffffff',
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                      position: 'relative',
                      height: { xs: '280px', md: '380px' },
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="/screenshots/task-dialog.png"
                      alt="Task creation dialog with all options"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'top center',
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999; background: #f5f5f5; font-size: 16px;">Task Dialog</div>';
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      bgcolor: '#ffffff',
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                      position: 'relative',
                      height: { xs: '280px', md: '380px' },
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="/screenshots/category-modal.png"
                      alt="Category creation modal with icon selection"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'top center',
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999; background: #f5f5f5; font-size: 16px;">Category Modal</div>';
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      bgcolor: '#ffffff',
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                      position: 'relative',
                      height: { xs: '280px', md: '380px' },
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="/screenshots/settings.png"
                      alt="Settings modal with theme and sound options"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'top center',
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999; background: #f5f5f5; font-size: 16px;">Settings</div>';
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Fade>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              color: 'rgba(0, 0, 0, 0.65)',
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.1rem' },
            }}
          >
            Simple, intuitive interface designed for focus
          </Typography>
        </Container>
      </Box>

      {/* Pricing Chart Section */}
      <Box sx={{ bgcolor: '#fafbfc', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <PricingChart />
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box sx={{ bgcolor: '#fafbfc', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="md">
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  fontWeight: 600,
                  mb: 4,
                  color: '#1a1a1a',
                  letterSpacing: '-0.01em',
                }}
              >
                Ready to stay ahead?
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  mb: 3,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Start using Ahead
                </Button>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(0, 0, 0, 0.65)',
                  fontSize: '1rem',
                }}
              >
                Already have an account?{' '}
                <Button
                  component={RouterLink}
                  to="/login"
                  sx={{
                    textTransform: 'none',
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': {
                      bgcolor: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Log in
                </Button>
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: '#ffffff',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', sm: 'center' },
              gap: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#1a1a1a',
                fontSize: '1.25rem',
              }}
            >
              KeepAhead
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(0, 0, 0, 0.55)',
                fontSize: '0.85rem',
              }}
            >
              © {new Date().getFullYear()} KeepAhead. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;

